import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Alert,
  CircularProgress, FormControlLabel, Switch, IconButton, Avatar
} from '@mui/material';
import { Save, ArrowBack, PhotoCamera } from '@mui/icons-material';
import api from '../services/api';

export default function ExpertEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [expert, setExpert] = useState({
    title: '',
    firstName: '',
    lastName: '',
    specialty: '',
    institution: '',
    city: '',
    country: 'France',
    bio: '',
    email: '',
    linkedin: '',
    image: '',
    isFeatured: false,
    isActive: true
  });

  useEffect(() => {
    if (!isNew) {
      fetchExpert();
    }
  }, [id]);

  const fetchExpert = async () => {
    try {
      const res = await api.get(`/experts/${id}`);
      setExpert(res.data);
    } catch (err) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setExpert({ ...expert, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      if (isNew) {
        await api.post('/experts', expert);
        setSuccess('Expert cree avec succes');
      } else {
        await api.put(`/experts/${id}`, expert);
        setSuccess('Expert mis a jour');
      }
      setTimeout(() => navigate('/experts'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/experts')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          {isNew ? 'Nouvel Expert' : 'Modifier Expert'}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} display="flex" justifyContent="center">
              <Box textAlign="center">
                <Avatar
                  src={expert.image}
                  sx={{ width: 120, height: 120, mb: 1, mx: 'auto' }}
                >
                  {expert.firstName?.charAt(0)}{expert.lastName?.charAt(0)}
                </Avatar>
                <TextField
                  size="small"
                  label="URL Photo"
                  value={expert.image || ''}
                  onChange={(e) => handleChange('image', e.target.value)}
                  placeholder="https://..."
                  sx={{ width: 300 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Titre"
                value={expert.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Dr / Pr"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Prenom"
                value={expert.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Nom"
                value={expert.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Specialite"
                value={expert.specialty}
                onChange={(e) => handleChange('specialty', e.target.value)}
                required
                placeholder="Cardiologue, Interniste..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Institution"
                value={expert.institution}
                onChange={(e) => handleChange('institution', e.target.value)}
                placeholder="CHU de Rouen"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ville"
                value={expert.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pays"
                value={expert.country}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Biographie"
                value={expert.bio || ''}
                onChange={(e) => handleChange('bio', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={expert.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="LinkedIn"
                value={expert.linkedin || ''}
                onChange={(e) => handleChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/in/..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={expert.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                }
                label="Actif"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={expert.isFeatured}
                    onChange={(e) => handleChange('isFeatured', e.target.checked)}
                  />
                }
                label="Mis en avant"
              />
            </Grid>
          </Grid>
        </Paper>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            type="submit"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={saving}
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/experts')}>
            Annuler
          </Button>
        </Box>
      </form>
    </Box>
  );
}
